import React, { useEffect } from 'react';
import './TextMessaging.css';

const REGISTER_URL = 'https://app.getgolfmarshal.com/register';

function TextMessaging() {
  useEffect(() => {
    document.title = 'Golf Marshal Text | SMS for Golf Courses';
    return () => {
      document.title = 'Golf Marshal | Professional Golf Tournament & League Management Software';
    };
  }, []);

  return (
    <div className="text-messaging-page">
      <section className="hero">
        <div className="hero-badge">⛳ Text Marketing for Golf Courses</div>
        <h1>
          Keep Every Golfer Informed.
          <br />
          Right From Your Course.
        </h1>
        <p>
          Send texts from your course&apos;s landline number. Tee time reminders, weather alerts, tournament updates —
          delivered instantly.
        </p>
        <div className="hero-actions">
          <a href={REGISTER_URL} className="btn-primary">
            Try Golf Marshal Free
          </a>
          <a href="mailto:dthomas@getgolfmarshal.com" className="btn-secondary">
            Schedule a Demo
          </a>
        </div>
        <p className="hero-note">Included in Pro at $189/mo · No separate texting fees</p>
      </section>

      <div className="customers">
        <p>Trusted by public courses across the country</p>
        <div className="customer-list">
          <span>Oak Valley Golf Club</span>
          <span>Sugar Creek Golf Course</span>
          <span>Crystal Highlands</span>
          <span>Ste. Genevieve CC</span>
          <span>Perryville CC</span>
        </div>
      </div>

      <section className="gm-section-white">
        <div className="section-inner">
          <p className="section-label">The problem</p>
          <h2 className="section-title">Golfers are hard to reach. Until now.</h2>
          <p className="section-sub">Phone calls go unanswered. Emails get buried. Facebook posts get missed.</p>
          <div className="pain-grid">
            <div className="pain-card">
              <div className="pain-icon">🚫</div>
              <h3>No-shows cost you revenue</h3>
              <p>A quick reminder text 24 hours out cuts no-shows dramatically. Without it, that tee time goes empty.</p>
            </div>
            <div className="pain-card">
              <div className="pain-icon">⛈️</div>
              <h3>Weather delays have no fast lane</h3>
              <p>When a storm rolls in, your staff is playing phone tag. Golfers show up anyway — then leave frustrated.</p>
            </div>
            <div className="pain-card">
              <div className="pain-icon">🔇</div>
              <h3>Tournaments end and the relationship goes quiet</h3>
              <p>You ran a great event. Players had fun. But you never hear from them again.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="how">
        <div className="section-inner">
          <p className="section-label">How it works</p>
          <h2 className="section-title">Set up in minutes. Text in seconds.</h2>
          <p className="section-sub">
            Golf Marshal Text is built right into your dashboard — no third-party tool, no separate bill.
          </p>
          <div className="steps">
            <div className="step">
              <div className="step-num">1</div>
              <h3>Import your players</h3>
              <p>Your golfer list syncs directly from Golf Marshal. Every booked player is ready to text.</p>
            </div>
            <div className="step">
              <div className="step-num">2</div>
              <h3>Write your message</h3>
              <p>Send from your course&apos;s real landline number. Personalize with first names. Schedule or send instantly.</p>
            </div>
            <div className="step">
              <div className="step-num">3</div>
              <h3>Track and respond</h3>
              <p>See delivery status and replies in real time. Opt-outs managed automatically.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="gm-section-white">
        <div className="section-inner">
          <p className="section-label">Real messages. Real results.</p>
          <h2 className="section-title">Every scenario your course actually faces.</h2>
          <div className="usecase-grid">
            <div className="usecase-card">
              <h3>⏰ Tee Time Reminder</h3>
              <div className="sms-header">
                <div className="sms-avatar">GM</div>
                <div className="sms-sender">Oak Valley Golf Club</div>
              </div>
              <div className="sms-bubble">
                Hi Mark! Your 10:30am tee time at Oak Valley is confirmed for tomorrow. See you on the first tee ⛳ Reply
                STOP to opt out.
              </div>
              <div className="sms-time">Delivered · 3:12 PM</div>
            </div>
            <div className="usecase-card">
              <h3>⛈️ Weather Delay Alert</h3>
              <div className="sms-header">
                <div className="sms-avatar">GM</div>
                <div className="sms-sender">Oak Valley Golf Club</div>
              </div>
              <div className="sms-bubble">
                Oak Valley Golf: Course is on weather delay until 2pm due to lightning. We&apos;ll text you when we&apos;re
                cleared to resume!
              </div>
              <div className="sms-time">Delivered · 11:47 AM</div>
            </div>
            <div className="usecase-card">
              <h3>🏆 Post-Tournament Follow-Up</h3>
              <div className="sms-header">
                <div className="sms-avatar">GM</div>
                <div className="sms-sender">Oak Valley Golf Club</div>
              </div>
              <div className="sms-bubble">
                Thanks for playing in our Labor Day Classic, Sarah! Final results are up at oakvalley.com/results. See you
                next round!
              </div>
              <div className="sms-time">Delivered · 5:30 PM</div>
            </div>
            <div className="usecase-card">
              <h3>🎯 Promo Offer</h3>
              <div className="sms-header">
                <div className="sms-avatar">GM</div>
                <div className="sms-sender">Oak Valley Golf Club</div>
              </div>
              <div className="sms-bubble">
                🏌️ Oak Valley: Book any tee time this week and get a free range bucket. Book at oakvalley.com/book.
                Offer ends Sunday.
              </div>
              <div className="sms-time">Delivered · 9:00 AM</div>
            </div>
          </div>
        </div>
      </section>

      <section className="proof">
        <div className="section-inner">
          <p className="section-label gm-text-center">What courses are saying</p>
          <h2 className="section-title gm-text-center">Built for courses like yours.</h2>
          <div className="proof-grid">
            <div className="proof-card">
              <div className="proof-stars">★★★★★</div>
              <blockquote>
                &ldquo;Finally a platform that handles tournaments and lets us text players the same day. We cut our
                no-show rate in half.&rdquo;
              </blockquote>
              <div className="proof-author">General Manager</div>
              <div className="proof-course">Oak Valley Golf Club</div>
            </div>
            <div className="proof-card">
              <div className="proof-stars">★★★★★</div>
              <blockquote>
                &ldquo;When weather rolls in, I used to spend 30 minutes calling down the sheet. Now I send one text and
                everyone knows in 10 seconds.&rdquo;
              </blockquote>
              <div className="proof-author">Head Pro</div>
              <div className="proof-course">Sugar Creek Golf Course</div>
            </div>
            <div className="proof-card">
              <div className="proof-stars">★★★★★</div>
              <blockquote>
                &ldquo;Text from your actual course number — not some random short code. Players respond. That detail
                matters more than you&apos;d think.&rdquo;
              </blockquote>
              <div className="proof-author">Tournament Director</div>
              <div className="proof-course">Crystal Highlands Golf Course</div>
            </div>
          </div>
        </div>
      </section>

      <section className="gm-section-white">
        <div className="section-inner">
          <p className="section-label gm-text-center">Simple pricing</p>
          <h2 className="section-title gm-text-center">
            Everything included.
            <br />
            No nickel-and-diming.
          </h2>
          <div className="pricing-card">
            <div className="pricing-badge">MOST POPULAR</div>
            <div className="pricing-name">Pro Plan</div>
            <div className="pricing-amount">
              <sup>$</sup>189
            </div>
            <div className="pricing-period">per month</div>
            <ul className="pricing-features">
              <li>
                <strong>Text Marketing</strong> — send from your course number
              </li>
              <li>Tournament management &amp; registration</li>
              <li>League management</li>
              <li>Tee time scheduling</li>
              <li>Player database &amp; history</li>
              <li>Automated tee time reminders</li>
              <li>Scorecards &amp; leaderboards</li>
              <li>Priority support</li>
            </ul>
            <a href={REGISTER_URL} className="btn-green">
              Start Free Trial
            </a>
            <p className="pricing-note">No credit card required · Cancel anytime</p>
          </div>
        </div>
      </section>

      <div className="final-cta">
        <h2>Ready to start texting your golfers?</h2>
        <p>Start a free trial today. Your first campaign can go out before the weekend.</p>
        <div className="final-actions">
          <a href={REGISTER_URL} className="btn-primary">
            Start Free Trial
          </a>
          <a href="mailto:dthomas@getgolfmarshal.com" className="btn-secondary">
            Schedule a Demo
          </a>
        </div>
      </div>

      <footer>
        <div className="footer-logo">Golf Marshal</div>
        <p>
          © {new Date().getFullYear()} Golf Marshal · A BHI Software product ·{' '}
          <a href="mailto:dthomas@getgolfmarshal.com">dthomas@getgolfmarshal.com</a>
        </p>
        <p style={{ marginTop: '8px' }}>
          <a href="https://getgolfmarshal.com">getgolfmarshal.com</a> · NGCOA Member
        </p>
      </footer>
    </div>
  );
}

export default TextMessaging;
